import { useState, useEffect } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { breedService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import type { Breed } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";

export default function BreedsPage() {
  const isOwner = useIsOwner();
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Breed | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");

  const loadBreeds = async () => {
    try {
      const data = await breedService.list();
      setBreeds(data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBreeds();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setError("");
    setModalOpen(true);
  };

  const openEdit = (breed: Breed) => {
    setEditing(breed);
    setName(breed.name);
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (editing) {
        await breedService.update(editing.id, { name });
      } else {
        await breedService.create({ name });
      }
      setModalOpen(false);
      await loadBreeds();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageMeta title="Breeds | Poultry Book" description="Manage bird breeds" />
      <PageBreadcrumb pageTitle="Breeds" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            All Breeds
          </h3>
          {isOwner && (
            <Button size="sm" onClick={openCreate}>
              Add Breed
            </Button>
          )}
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : breeds.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No breeds found. Add your first breed.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Name
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Created
                  </TableCell>
                  {isOwner && (
                    <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {breeds.map((breed) => (
                  <TableRow
                    key={breed.id}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <TableCell className="px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                      {breed.name}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(breed.created_at).toLocaleDateString("en-IN")}
                    </TableCell>
                    {isOwner && (
                      <TableCell className="px-5 py-3 text-right">
                        <button
                          onClick={() => openEdit(breed)}
                          className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                        >
                          Edit
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        className="max-w-md p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit Breed" : "Add Breed"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="breedName">Breed Name *</Label>
            <Input
              id="breedName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Skylark Bovans, BV-380"
              required
              maxLength={100}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !name.trim()}>
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
